import React, { useEffect, useState } from "react";
import { useSEO } from "@/hooks/useSEO";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, FileText, ShieldCheck, Share2 } from "lucide-react";
import api, { apiError, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

export function ApplyForm({ campaignId, linkId, onDone }) {
  const [f, setF] = useState({ full_name: "", mobile: "", email: "", pincode: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/applications", { campaign_id: campaignId, link_id: linkId || null, ...f });
      if (data.redirect_url) {
        toast.success("Application received! Redirecting to the brand page…");
        window.location.href = data.redirect_url;
        return;
      }
      setDone(data);
      toast.success("Application submitted!");
      onDone && onDone(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };
  if (done) return (
    <div className="text-center py-6" data-testid="apply-success">
      <CheckCircle2 className="w-14 h-14 text-teal-500 mx-auto mb-3" />
      <h3 className="font-display font-bold text-xl text-[#0B223D]">Application received</h3>
      <p className="text-slate-500 mt-1">Reference: <b className="font-mono-num">{done.submission_id}</b></p>
      <p className="text-sm text-slate-400 mt-2">Our team will verify and process your application.</p>
    </div>
  );
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="apply-form">
      {[["full_name", "Full Name", "text"], ["mobile", "Mobile Number", "tel"], ["email", "Email (optional)", "email"], ["pincode", "Pincode", "text"]].map(([k, label, type]) => (
        <div key={k}>
          <Label className="text-sm font-semibold text-slate-600">{label}</Label>
          <Input type={type} required={k !== "email"} value={f[k]} data-testid={`apply-${k}`}
            minLength={k === "full_name" ? 3 : undefined}
            maxLength={k === "mobile" ? 10 : k === "pincode" ? 6 : k === "full_name" ? 60 : undefined}
            onChange={(e) => {
              let v = e.target.value;
              if (k === "full_name") v = v.replace(/[^A-Za-z\s.'-]/g, "").slice(0, 60);
              if (k === "mobile") v = v.replace(/\D/g, "").slice(0, 10);
              if (k === "pincode") v = v.replace(/\D/g, "").slice(0, 6);
              setF({ ...f, [k]: v });
            }} className="mt-1.5 h-11" placeholder={label} />
        </div>
      ))}
      <Button type="submit" disabled={loading} data-testid="apply-submit-btn"
        className="w-full bg-[#0B223D] hover:bg-[#06182B] text-white font-bold h-12 rounded-xl">
        {loading ? "Submitting…" : "Submit Application"}
      </Button>
    </form>
  );
}

export default function ProductDetail() {
  const { pid } = useParams();
  const [p, setP] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();
  useSEO(p ? { title: `${p.name} — Apply Online`, path: `/products/${pid}`, description: (p.description || `Apply for ${p.name} on Credit Kendra.`).slice(0, 160), image: p.image || undefined, keywords: `${p.name}, ${p.category}, apply online, Credit Kendra` } : {});
  useEffect(() => {
    api.get(`/products/${pid}`).then((r) => {
      setP(r.data);
      let vid = localStorage.getItem("omi_vid");
      if (!vid) { vid = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("omi_vid", vid); }
      api.post("/track/activity", { visitor_id: vid, event: "view", category: r.data?.category || "",
        product_id: pid, page: window.location.pathname }).catch(() => {});
    }).catch(() => {});
    api.get("/campaigns/public").then((r) => setCampaign(r.data.find((c) => c.product_id === pid))).catch(() => {});
  }, [pid]);
  if (!p) return <div className="min-h-[60vh] flex items-center justify-center text-slate-400">Loading…</div>;
  return (
    <div className="max-w-[1280px] mx-auto px-6 py-12">
      <div className="grid lg:grid-cols-2 gap-10">
        <div>
          <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-lg">
            <img src={p.image} alt={p.name} className="w-full h-72 object-cover" />
          </div>
          <span className="text-xs font-bold uppercase tracking-wider text-emerald-brand mt-6 block">{p.category}</span>
          <h1 className="font-display text-3xl font-extrabold text-[#0B223D] mt-1">{p.name}</h1>
          <p className="text-slate-500 mt-3 text-lg">{p.description}</p>

          {campaign && (
            <div className="mt-5 inline-flex items-center gap-2 bg-teal-50 text-teal-700 font-bold px-4 py-2 rounded-full">
              <Share2 className="w-4 h-4" /> Refer &amp; earn up to {inr(campaign.payout_pool)} per conversion
            </div>
          )}

          <div className="mt-8 grid sm:grid-cols-2 gap-6">
            <div>
              <h4 className="font-display font-bold text-[#0B223D] flex items-center gap-2 mb-3"><CheckCircle2 className="w-4 h-4 text-teal-500" /> Features</h4>
              <ul className="space-y-2">{p.features.map((x) => <li key={x} className="text-sm text-slate-600 flex gap-2"><span className="text-teal-500">•</span>{x}</li>)}</ul>
            </div>
            <div>
              <h4 className="font-display font-bold text-[#0B223D] flex items-center gap-2 mb-3"><FileText className="w-4 h-4 text-teal-500" /> Documents</h4>
              <ul className="space-y-2">{p.documents.map((x) => <li key={x} className="text-sm text-slate-600 flex gap-2"><span className="text-teal-500">•</span>{x}</li>)}</ul>
            </div>
          </div>
          <div className="mt-6 bg-slate-50 rounded-xl p-4 border border-slate-200">
            <h4 className="font-semibold text-[#0B223D] flex items-center gap-2 text-sm"><ShieldCheck className="w-4 h-4 text-teal-500" /> Eligibility</h4>
            <p className="text-sm text-slate-600 mt-1 whitespace-pre-line">{p.eligibility}</p>
          </div>

          {campaign?.flow?.length > 0 && (
            <div className="mt-6" data-testid="campaign-flow">
              <h4 className="font-display font-bold text-[#0B223D] mb-3">Campaign Flow — how it works</h4>
              <ol className="space-y-3">
                {campaign.flow.map((step, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="w-7 h-7 shrink-0 rounded-full bg-[#0B223D] text-white font-display font-bold text-sm flex items-center justify-center">{i + 1}</span>
                    <span className="text-sm text-slate-600 pt-1">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>

        <div>
          <div className="sticky top-24 bg-white rounded-3xl border border-slate-200 shadow-xl p-7">
            <h3 className="font-display font-bold text-xl text-[#0B223D] mb-1">Apply for {p.name}</h3>
            <p className="text-sm text-slate-400 mb-3">Takes less than 2 minutes.</p>
            <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4" data-testid="product-payment-note">
              <ShieldCheck className="w-4 h-4 shrink-0" />
              Payment will be released only after confirmation from the brand.
            </div>
            {campaign ? <ApplyForm campaignId={campaign.id} /> : <p className="text-slate-400 text-sm">Applications open soon.</p>}
            {user && user !== false && campaign && (
              <Button variant="outline" onClick={() => nav("/refer")} data-testid="product-get-link-btn"
                className="w-full mt-3 border-teal-300 text-teal-700 font-semibold h-11 rounded-xl">
                <Share2 className="w-4 h-4 mr-1.5" /> Get your referral link instead
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
